import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { setCredentials, logOut, updateUser } from '../features/auth/authSlice';
import { uploadWithProgress, API_BASE } from '../lib/uploadWithProgress';

const buildUploadQueryFn = (buildPath, fileField) =>
  async (arg, api) => {
    const { onProgress, signal, ...rest } = arg;
    const file = rest.videoFile || rest.coverFile || rest.thumbnailFile || rest.avatarFile || rest.file;
    if (!file) return { error: { status: 400, data: { message: 'Файл не предоставлен' } } };

    const formData = new FormData();
    formData.append(fileField, file);
    if (rest.durationMinutes !== undefined) formData.append('durationMinutes', rest.durationMinutes);

    const token = api.getState().auth.token || localStorage.getItem('accessToken');
    try {
      const { data } = await uploadWithProgress({
        url: `${API_BASE}${buildPath(rest)}`,
        method: 'POST',
        formData,
        token,
        onProgress,
        signal,
      });
      return { data };
    } catch (err) {
      return { error: { status: err.status || 500, data: err.data || { message: 'Ошибка загрузки' } } };
    }
  };

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  // credentials: 'include' — критично для httpOnly cookie с refresh-токеном.
  // Без этого браузер не пошлёт cookie на /auth/refresh, и refresh не сработает.
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token || localStorage.getItem('accessToken');
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Refresh-токен теперь в httpOnly cookie — JS его не видит и слать в body
    // не должен. Просто дёргаем /auth/refresh с пустым body, cookie прицепится
    // автоматически благодаря `credentials: 'include'`.
    const refreshResult = await baseQuery(
      { url: '/auth/refresh', method: 'POST', body: {} },
      api,
      extraOptions
    );
    if (refreshResult.data) {
      const { user, accessToken } = refreshResult.data;
      api.dispatch(setCredentials({ user, accessToken }));
      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logOut());
    }
  }
  return result;
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: ['InstructorCourses', 'MyCourses', 'InstructorProfile', 'Users', 'Me', 'Course', 'Categories', 'PendingCourses', 'CourseAccess', 'CourseContent', 'OrderStatus', 'PaymentStatus', 'PublicInstructor', 'MyQuizzes', 'Quiz', 'QuizStats', 'AdminUsers', 'CourseReviews', 'MyReview', 'AdminReviews', 'MyCertificates', 'MyPendingOrders', 'InstructorFinance', 'InstructorSales', 'InstructorPayouts', 'AdminP2pOrders', 'AdminPayouts', 'AdminUserOrders', 'MySessions', 'AdminAuditLog', 'Notifications'],
  endpoints: (builder) => ({
    // --- AUTH ---
    login: builder.mutation({ query: (credentials) => ({ url: '/auth/login', method: 'POST', body: credentials }) }),
    register: builder.mutation({ query: (data) => ({ url: '/auth/register', method: 'POST', body: data }) }),
    verify: builder.mutation({ query: (data) => ({ url: '/auth/verify', method: 'POST', body: data }) }),
    getMe: builder.query({ query: () => '/auth/me', providesTags: ['Me'] }),
    forgotPassword: builder.mutation({
      query: (payload) => ({ url: '/auth/forgot-password', method: 'POST', body: payload }),
    }),
    resetPassword: builder.mutation({
      query: ({ token, payload }) => ({ url: `/auth/reset-password/${token}`, method: 'POST', body: payload }),
    }),
    // Смена пароля для залогиненного юзера: требует ввод текущего пароля.
    // Это критично — без этого XSS-уязвимость позволила бы угнать аккаунт.
    changePassword: builder.mutation({
      query: ({ currentPassword, newPassword }) => ({
        url: '/auth/change-password',
        method: 'POST',
        body: { currentPassword, newPassword },
      }),
    }),
    logout: builder.mutation({
      // Серверный logout — дёргает /auth/logout чтобы бэк стёр httpOnly cookie
      // с refresh-токеном. Затем чистим redux/localStorage.
      query: () => ({ url: '/auth/logout', method: 'POST', body: {} }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // Даже если backend упал, локально разлогиниваем — не блокируем юзера.
        } finally {
          dispatch(logOut());
        }
      },
      invalidatesTags: ['Me', 'MyCourses', 'InstructorCourses'],
    }),
    // Запрос на удаление аккаунта — ставит 30-дневный grace. Юзер всё ещё
    // может войти и отменить.
    requestAccountDeletion: builder.mutation({
      query: () => ({ url: '/auth/account/request-deletion', method: 'POST', body: {} }),
      invalidatesTags: ['Me'],
    }),
    cancelAccountDeletion: builder.mutation({
      query: () => ({ url: '/auth/account/cancel-deletion', method: 'POST', body: {} }),
      invalidatesTags: ['Me'],
    }),

    // --- АКТИВНЫЕ СЕССИИ (Settings → «Мои устройства») ---
    getMySessions: builder.query({
      query: () => '/auth/sessions',
      providesTags: ['MySessions'],
    }),
    revokeSession: builder.mutation({
      query: (id) => ({ url: `/auth/sessions/${id}`, method: 'DELETE' }),
      invalidatesTags: ['MySessions'],
    }),

    // --- ADMIN AUDIT LOG ---
    getAuditLogRecent: builder.query({
      query: () => '/admin/audit-log/recent',
      providesTags: ['AdminAuditLog'],
    }),
    getAuditLog: builder.query({
      query: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.page) qs.set('page', String(params.page));
        if (params.limit) qs.set('limit', String(params.limit));
        if (params.action) qs.set('action', params.action);
        if (params.actorId) qs.set('actorId', params.actorId);
        if (params.targetType) qs.set('targetType', params.targetType);
        if (params.targetId) qs.set('targetId', params.targetId);
        if (params.from) qs.set('from', params.from);
        if (params.to) qs.set('to', params.to);
        if (params.search) qs.set('search', params.search);
        const s = qs.toString();
        return `/admin/audit-log${s ? `?${s}` : ''}`;
      },
      providesTags: ['AdminAuditLog'],
    }),
    getAuditLogMeta: builder.query({
      query: () => '/admin/audit-log/meta',
    }),

    // --- IN-APP NOTIFICATIONS ---
    getNotifications: builder.query({
      query: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.page) qs.set('page', String(params.page));
        if (params.limit) qs.set('limit', String(params.limit));
        if (params.unreadOnly) qs.set('unreadOnly', 'true');
        const s = qs.toString();
        return `/notifications${s ? `?${s}` : ''}`;
      },
      providesTags: ['Notifications'],
    }),
    getNotificationUnreadCount: builder.query({
      query: () => '/notifications/unread-count',
      providesTags: ['Notifications'],
    }),
    markNotificationRead: builder.mutation({
      query: (id) => ({ url: `/notifications/${id}/read`, method: 'POST' }),
      invalidatesTags: ['Notifications'],
    }),
    markAllNotificationsRead: builder.mutation({
      query: () => ({ url: '/notifications/mark-all-read', method: 'POST' }),
      invalidatesTags: ['Notifications'],
    }),

    // --- INSTRUCTOR COURSES ---
    getInstructorCourses: builder.query({
      query: () => '/courses',
      providesTags: ['InstructorCourses'],
    }),
    createCourse: builder.mutation({
      query: (courseData) => ({ url: '/courses', method: 'POST', body: courseData }),
      invalidatesTags: ['InstructorCourses'],
    }),
    getCourseForEditing: builder.query({
      query: (courseId) => `/courses/${courseId}`,
      providesTags: (result, error, id) => [{ type: 'Course', id }],
    }),
    updateCourseDetails: builder.mutation({
      query: ({ courseId, ...patch }) => ({ url: `/courses/${courseId}`, method: 'PUT', body: patch }),
      invalidatesTags: (result, error, { courseId }) => [{ type: 'Course', id: courseId }, 'InstructorCourses'],
    }),
   
    updateCourseCurriculum: builder.mutation({
      query: ({ courseId, sections }) => ({
        url: `/courses/${courseId}/curriculum`,
        method: 'PUT',
        body: { sections },
      }),
      invalidatesTags: (result, error, { courseId }) => [{ type: 'Course', id: courseId }],
    }),
    addSection: builder.mutation({
      query: ({ courseId, title }) => ({
        url: `/courses/${courseId}/sections`,
        method: 'POST',
        body: { title },
      }),
      // НЕ инвалидируем Course-кэш: refetch стирал бы локально набранный, но
      // ещё не сохранённый текст в редакторе. Серверная версия раздела уже
      // вернётся в response.unwrap() и будет добавлена в локальный state.
    }),

    addLesson: builder.mutation({
      query: ({ courseId, sectionId, title }) => ({
        url: `/courses/${courseId}/sections/${sectionId}/lessons`,
        method: 'POST',
        body: { title },
      }),
      // То же самое — без инвалидации, чтобы не было refetch'а с потерей текста.
    }),
    deleteSection: builder.mutation({
      query: ({ courseId, sectionId }) => ({
        url: `/courses/${courseId}/sections/${sectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { courseId }) => [{ type: 'Course', id: courseId }],
    }),
    deleteLesson: builder.mutation({
      query: ({ courseId, sectionId, lessonId }) => ({
        url: `/courses/${courseId}/sections/${sectionId}/lessons/${lessonId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { courseId }) => [{ type: 'Course', id: courseId }],
    }),
    deleteLessonVideo: builder.mutation({
      query: ({ courseId, lessonId }) => ({
        url: `/uploads/courses/${courseId}/lessons/${lessonId}/video`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { courseId }) => [{ type: 'Course', id: courseId }],
    }),
    deletePreviewVideo: builder.mutation({
      query: ({ courseId }) => ({
        url: `/uploads/courses/${courseId}/preview-video`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { courseId }) => [{ type: 'Course', id: courseId }],
    }),
    submitCourseForReview: builder.mutation({
      query: (courseId) => ({ url: `/courses/${courseId}/review`, method: 'POST' }), // Fixed endpoint
      invalidatesTags: (result, error, courseId) => [{ type: 'Course', id: courseId }, 'InstructorCourses'],
    }),
    deleteCourse: builder.mutation({
      query: (courseId) => ({ url: `/courses/${courseId}`, method: 'DELETE' }),
      invalidatesTags: ['InstructorCourses'],
    }),
    uploadPreviewVideo: builder.mutation({
      // queryFn enables real upload progress via XHR.
      queryFn: buildUploadQueryFn(({ courseId }) => `/courses/${courseId}/preview-video`, 'video'),
      invalidatesTags: (result, error, { courseId }) => [{ type: 'Course', id: courseId }],
    }),

    // --- UPLOADS (with progress) ---
    uploadAvatar: builder.mutation({
      queryFn: buildUploadQueryFn(() => `/uploads/avatar`, 'avatar'),
      invalidatesTags: ['Me'],
      // После успешной загрузки СРАЗУ мерджим новый avatarUrl в Redux auth.
      // Без этого шапка показывала старый аватар до релогина: header читает
      // user.avatarUrl из state.auth.user (initialized из localStorage при логине),
      // а 'Me'-кэш — это RTK Query, отдельный store. Дублируем фикс в auth Redux,
      // чтобы все компоненты увидели апдейт без перезагрузки.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const avatarUrl = data?.user?.avatarUrl || data?.avatarUrl || data?.url;
          if (avatarUrl) dispatch(updateUser({ avatarUrl }));
        } catch { /* mutation сам обработает ошибку */ }
      },
    }),
    uploadCourseCover: builder.mutation({
      queryFn: buildUploadQueryFn(({ courseId }) => `/uploads/courses/${courseId}/cover`, 'cover'),
      invalidatesTags: (result, error, { courseId }) => [{ type: 'Course', id: courseId }],
    }),
    uploadLessonVideo: builder.mutation({
      queryFn: buildUploadQueryFn(
        ({ courseId, lessonId }) => `/uploads/courses/${courseId}/lessons/${lessonId}/video`,
        'video'
      ),
      invalidatesTags: (result, error, { courseId }) => [{ type: 'Course', id: courseId }],
    }),

    // --- STUDENT & PROFILE ---
    getMyCourses: builder.query({
      query: () => '/auth/my-courses',
      providesTags: ['MyCourses'],
    }),
    getInstructorProfile: builder.query({
      query: () => '/instructor/profile/me',
      providesTags: ['InstructorProfile'],
    }),
    updateInstructorProfile: builder.mutation({
      query: (profileData) => ({ url: '/instructor/profile', method: 'PUT', body: profileData }),
      invalidatesTags: ['InstructorProfile'],
    }),
     getInstructorPublicProfile: builder.query({
      query: (instructorId) => `/instructor/${instructorId}/profile`,
      providesTags: ['PublicInstructor'],
    }),

    // --- CATEGORIES (ADMIN) ---
    getAllCategories: builder.query({
      query: () => '/categories',
      providesTags: ['Categories'],
    }),
    createCategory: builder.mutation({
      query: (newCategory) => ({ url: '/categories', method: 'POST', body: newCategory }),
      invalidatesTags: ['Categories'],
    }),
    updateCategory: builder.mutation({
      query: ({ id, ...patch }) => ({ url: `/categories/${id}`, method: 'PUT', body: patch }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Categories', id }],
    }),
    deleteCategory: builder.mutation({
      query: (id) => ({ url: `/categories/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Categories'],
    }),
    getPendingCourses: builder.query({
      query: () => '/admin/courses/pending',
      providesTags: ['PendingCourses'],
    }),
    updateCourseStatus: builder.mutation({
      query: ({ courseId, status, rejectionReason }) => ({
        url: `/admin/courses/${courseId}/status`,
        method: 'PATCH',
        body: { status, rejectionReason },
      }),
      invalidatesTags: ['PendingCourses', 'InstructorCourses'],
    }),
    adminDeleteCourse: builder.mutation({
      query: (courseId) => ({ url: `/admin/courses/${courseId}`, method: 'DELETE' }),
      invalidatesTags: ['PendingCourses', 'InstructorCourses'],
    }),
    getAdminCourseFull: builder.query({
      query: (courseId) => `/admin/courses/${courseId}/full`,
      providesTags: (result, error, courseId) => [{ type: 'Course', id: courseId }],
    }),
    getAdminDashboard: builder.query({
      query: () => '/admin/dashboard',
      // Не keep'аем кэш слишком долго — но и не авто-рефетчим.
      // Пользователь сам жмёт «Обновить» в UI; бэк уже кэширует health 30с.
      keepUnusedDataFor: 60,
    }),
    reconcileStorage: builder.mutation({
      query: (body = {}) => ({
        url: '/admin/storage/reconcile',
        method: 'POST',
        body,
      }),
    }),
    // Видео-джобы для админ-панели. Дёргается вручную (Refresh-кнопка),
    // без авто-polling'а — иначе на live processing-джобах будем долбить
    // бэк каждые 2с со всех открытых вкладок админок.
    getAdminVideoJobs: builder.query({
      query: (params = {}) => {
        const qp = new URLSearchParams();
        if (params.status) qp.append('status', params.status);
        if (params.limit) qp.append('limit', params.limit);
        return { url: '/admin/video-jobs', params: qp };
      },
      keepUnusedDataFor: 30,
    }),
    // Управление юзерами для админа: поиск + смена ролей.
    getAdminUsers: builder.query({
      query: (params = {}) => {
        const qp = new URLSearchParams();
        if (params.search) qp.append('search', params.search);
        if (params.role) qp.append('role', params.role);
        if (params.page) qp.append('page', params.page);
        if (params.limit) qp.append('limit', params.limit);
        return { url: '/admin/users', params: qp };
      },
      providesTags: ['AdminUsers'],
    }),
    updateAdminUserRoles: builder.mutation({
      query: ({ userId, add, remove }) => ({
        url: `/admin/users/${userId}/roles`,
        method: 'PATCH',
        body: { add, remove },
      }),
      invalidatesTags: ['AdminUsers'],
    }),
    // Аналитика инструктора: всё в одном запросе (summary + per-course
    // performance + revenue timeline + recent enrollments).
    getInstructorAnalytics: builder.query({
      query: ({ from = '', to = '', days } = {}) => {
        // Если переданы from/to — используем точный календарный интервал.
        // Иначе fallback на legacy ?days=N для обратной совместимости.
        const params = {};
        if (from) params.from = from;
        if (to) params.to = to;
        if (!from && !to && days) params.days = days;
        return { url: '/instructor/analytics', params };
      },
      keepUnusedDataFor: 60,
    }),
    // Полный пагинированный список заказов на курсах инструктора.
    // Используется на InstructorAnalyticsPage в секции «Покупки» —
    // вместо хардкоженных 10 из recentEnrollments.
    getInstructorOrders: builder.query({
      query: ({ page = 1, limit = 20, status } = {}) => {
        const qp = new URLSearchParams();
        qp.append('page', page);
        qp.append('limit', limit);
        if (status) qp.append('status', status);
        return { url: '/instructor/analytics/orders', params: qp };
      },
      keepUnusedDataFor: 30,
    }),
    // Полный пагинированный список ВСЕХ заказов платформы для админки.
    getAdminOrders: builder.query({
      query: ({ page = 1, limit = 20, status, search } = {}) => {
        const qp = new URLSearchParams();
        qp.append('page', page);
        qp.append('limit', limit);
        if (status) qp.append('status', status);
        if (search) qp.append('search', search);
        return { url: '/admin/orders', params: qp };
      },
      keepUnusedDataFor: 30,
    }),
    getPublicCourses: builder.query({
      // Сервер теперь пагинирует и фильтрует. Возвращает
      // { items, page, pageSize, total, pageCount }.
      query: (params = {}) => {
        const qp = new URLSearchParams();
        if (params.category) qp.append('category', params.category);
        if (params.language) qp.append('language', params.language);
        if (params.minPrice) qp.append('minPrice', params.minPrice);
        if (params.maxPrice) qp.append('maxPrice', params.maxPrice);
        if (params.sortBy) qp.append('sortBy', params.sortBy);
        if (params.search) qp.append('search', params.search);
        if (params.page) qp.append('page', params.page);
        if (params.limit) qp.append('limit', params.limit);
        return { url: 'customer/courses', params: qp };
      },
    }),
    getCourseById: builder.query({
      query: (courseId) => `customer/courses/${courseId}`,
    }),
       getPreviewVideo: builder.query({
  query: (courseId) => `customer/courses/${courseId}/preview-video`, // ✅ правильный путь
}),
    checkCourseAccess: builder.query({
      query: (courseId) => `customer/courses/${courseId}/access`,
      providesTags: (result, error, courseId) => [{ type: 'CourseAccess', id: courseId }],
    }),
    getPurchasedCourseContent: builder.query({
      query: (courseId) => `customer/courses/${courseId}/content`,
      providesTags: (result, error, courseId) => [{ type: 'CourseContent', id: courseId }],
    }),
    submitCourseLessonQuiz: builder.mutation({
      query: ({ courseId, lessonId, answers }) => ({
        url: `customer/courses/${courseId}/lessons/${lessonId}/quiz`,
        method: 'POST',
        body: { answers },
      }),
      // На успешный pass патчим CourseContent кэш: помечаем quiz-урок passed
      // и снимаем locked со следующих уроков. Без этого пришлось бы делать
      // полный refetch контента после каждой попытки.
      async onQueryStarted({ courseId, lessonId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (!data?.passed) return;
          dispatch(
            apiSlice.util.updateQueryData('getPurchasedCourseContent', courseId, (draft) => {
              const root = draft?.data;
              if (!root?.sections) return;
              const lid = String(lessonId);
              let unlock = false;
              for (const s of root.sections) {
                for (const l of s.lessons || []) {
                  if (String(l._id) === lid) {
                    l.attempt = {
                      passed: true,
                      percentage: data.percentage,
                      attempts: (l.attempt?.attempts || 0) + 1,
                    };
                    unlock = true;
                  } else if (unlock && l.locked) {
                    // На клиенте не можем восстановить videoUrl/theoryContent;
                    // ставим флаг, что нужно перезагрузить контент. Полный
                    // refetch — лучшая стратегия, чем «полузалоченные» данные.
                    l.locked = false;
                    l._needsRefetch = true;
                  }
                }
              }
            })
          );
          // Принудительный refetch, чтобы подтянуть свежие videoUrl/theoryContent
          // только что разлоченных уроков.
          dispatch(apiSlice.util.invalidateTags([{ type: 'CourseContent', id: courseId }]));
        } catch {
          /* server вернёт ошибку — она и так покажется в UI через unwrap */
        }
      },
      invalidatesTags: (result, error, { courseId }) => [
        { type: 'CourseContent', id: courseId },
      ],
    }),
    updateLessonProgress: builder.mutation({
      query: ({ courseId, lessonId, completed }) => ({
        url: `customer/courses/${courseId}/lessons/${lessonId}/progress`,
        method: 'POST',
        body: { completed },
      }),
      // Optimistic-обновление кэша CourseContent — UI не ждёт сети.
      async onQueryStarted({ courseId, lessonId, completed }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          apiSlice.util.updateQueryData('getPurchasedCourseContent', courseId, (draft) => {
            const data = draft?.data;
            if (!data) return;
            const set = new Set((data.completedLessons || []).map(String));
            if (completed) set.add(String(lessonId));
            else set.delete(String(lessonId));
            data.completedLessons = Array.from(set);
            data.completedCount = set.size;
            data.progress = data.totalLessons
              ? Math.round((set.size / data.totalLessons) * 100)
              : 0;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ['MyCourses'],
    }),
    purchaseCourse: builder.mutation({
      query: ({ userId, courseId }) => ({
        url: 'customer/orders',
        method: 'POST',
        body: { userId, courseId },
      }),
      invalidatesTags: (result, error, { courseId }) => [
        { type: 'CourseAccess', id: courseId },
        { type: 'CourseContent', id: courseId },
        'MyCourses',
      ],
    }),
    getOrderStatus: builder.query({
      query: (orderId) => `customer/orders/${orderId}`,
      providesTags: (result, error, orderId) => [{ type: 'OrderStatus', id: orderId }],
    }),
    verifyPayment: builder.query({
      query: (orderId) => `customer/orders/${orderId}/verify`,
      providesTags: (result, error, orderId) => [{ type: 'PaymentStatus', id: orderId }],
    }),

    // ── P2P ORDERS (manual flow) ────────────────────────────────
    // Студент → оставляет контакт → админ связывается → подтверждает оплату.
    // Эти endpoint'ы НЕ заменяют KaspiPay (createOrder выше остаётся как legacy),
    // используются параллельно. Активный flow выбирается в UI через флаг.
    requestP2pOrder: builder.mutation({
      query: ({ courseId, phone, hasWhatsapp, note }) => ({
        url: '/orders/p2p/request',
        method: 'POST',
        body: { courseId, phone, hasWhatsapp, note },
      }),
      invalidatesTags: (result, error, { courseId }) => [
        { type: 'CourseAccess', id: courseId },
        'MyPendingOrders',
      ],
    }),
    getMyPendingOrders: builder.query({
      query: () => '/orders/my-pending',
      providesTags: ['MyPendingOrders'],
    }),

    // ── INSTRUCTOR FINANCE ──────────────────────────────────────
    getInstructorFinanceOverview: builder.query({
      query: () => '/instructor/finance/overview',
      providesTags: ['InstructorFinance'],
    }),
    getInstructorSales: builder.query({
      query: ({ from = '', to = '', page = 1, limit = 15 } = {}) => {
        const qp = new URLSearchParams();
        if (from) qp.append('from', from);
        if (to) qp.append('to', to);
        qp.append('page', String(page));
        qp.append('limit', String(limit));
        return { url: `/instructor/finance/sales?${qp.toString()}` };
      },
      providesTags: ['InstructorSales'],
    }),
    getInstructorPayoutHistory: builder.query({
      query: ({ page = 1, limit = 10 } = {}) => {
        const qp = new URLSearchParams();
        qp.append('page', String(page));
        qp.append('limit', String(limit));
        return { url: `/instructor/finance/payouts?${qp.toString()}` };
      },
      providesTags: ['InstructorPayouts'],
    }),
    requestInstructorPayout: builder.mutation({
      query: (body) => ({
        url: '/instructor/payouts/request',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['InstructorFinance', 'InstructorPayouts', 'InstructorSales'],
    }),

    // ── ADMIN P2P (orders + payouts) ────────────────────────────
    getAdminPendingOrders: builder.query({
      query: (status = 'pending_contact') => `/admin/orders/pending?status=${status}`,
      providesTags: ['AdminP2pOrders'],
    }),
    adminMarkContacted: builder.mutation({
      query: (orderId) => ({
        url: `/admin/orders/${orderId}/contact`,
        method: 'POST',
      }),
      invalidatesTags: ['AdminP2pOrders'],
    }),
    adminConfirmOrder: builder.mutation({
      query: (orderId) => ({
        url: `/admin/orders/${orderId}/confirm`,
        method: 'POST',
      }),
      invalidatesTags: ['AdminP2pOrders', 'MyPendingOrders'],
    }),
    adminRejectOrder: builder.mutation({
      query: ({ orderId, reason }) => ({
        url: `/admin/orders/${orderId}/reject`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['AdminP2pOrders', 'MyPendingOrders'],
    }),
    // Возврат оплаченного заказа — ручная операция. Студент теряет доступ
    // (delete enrollment), статус заказа становится 'refunded' для compliance.
    adminRefundOrder: builder.mutation({
      query: ({ orderId, reason }) => ({
        url: `/admin/orders/${orderId}/refund`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['AdminP2pOrders', 'InstructorFinance', 'MyCourses', 'AdminUserOrders'],
    }),
    // Список заказов конкретного студента — для refund-страницы.
    getAdminUserOrders: builder.query({
      query: (userId) => `/admin/users/${userId}/orders`,
      providesTags: (result, error, userId) => [{ type: 'AdminUserOrders', id: userId }],
    }),
    getAdminPendingPayouts: builder.query({
      query: (status = 'pending') => `/admin/payouts/pending?status=${status}`,
      providesTags: ['AdminPayouts'],
    }),
    adminConfirmPayout: builder.mutation({
      query: ({ payoutId, kaspiReference, note }) => ({
        url: `/admin/payouts/${payoutId}/confirm`,
        method: 'POST',
        body: { kaspiReference, note },
      }),
      invalidatesTags: ['AdminPayouts', 'InstructorPayouts', 'InstructorFinance'],
    }),
    adminRejectPayout: builder.mutation({
      query: ({ payoutId, reason }) => ({
        url: `/admin/payouts/${payoutId}/reject`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['AdminPayouts', 'InstructorPayouts', 'InstructorFinance'],
    }),

    // ── REVIEWS ─────────────────────────────────────────────────
    // Публичный список отзывов курса + агрегат (avgRating, distribution).
    // Денормализованные avgRating/reviewsCount возвращаются и через
    // getCourseById (для шапки), и здесь (когда юзер открывает блок).
    getCourseReviews: builder.query({
      query: ({ courseId, page = 1, limit = 10 }) => {
        const qp = new URLSearchParams({ page, limit });
        return `/reviews/courses/${courseId}?${qp}`;
      },
      providesTags: (result, error, { courseId }) => [
        { type: 'CourseReviews', id: courseId },
      ],
    }),
    // Мой отзыв на курс + статус gate'а (canReview, progress, gate=50%).
    // Используется CourseDetailPage чтобы решить «показать форму / показать
    // pending / показать "доберите до 50%"».
    getMyReview: builder.query({
      query: (courseId) => `/reviews/courses/${courseId}/mine`,
      providesTags: (result, error, courseId) => [
        { type: 'MyReview', id: courseId },
      ],
    }),
    createReview: builder.mutation({
      query: ({ courseId, rating, text }) => ({
        url: `/reviews/courses/${courseId}`,
        method: 'POST',
        body: { rating, text },
      }),
      // После создания инвалидируем: свой отзыв + публичный список (хотя
      // pending не попадёт в список, кнопка «Написать отзыв» спрячется
      // по обновлённому MyReview).
      invalidatesTags: (result, error, { courseId }) => [
        { type: 'MyReview', id: courseId },
        { type: 'CourseReviews', id: courseId },
        { type: 'Course', id: courseId },
      ],
    }),
    updateMyReview: builder.mutation({
      query: ({ reviewId, courseId, rating, text }) => ({
        url: `/reviews/${reviewId}`,
        method: 'PUT',
        body: { rating, text },
      }),
      invalidatesTags: (result, error, { courseId }) => [
        { type: 'MyReview', id: courseId },
        { type: 'CourseReviews', id: courseId },
        { type: 'Course', id: courseId },
      ],
    }),
    deleteMyReview: builder.mutation({
      query: ({ reviewId }) => ({
        url: `/reviews/${reviewId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { courseId }) => [
        { type: 'MyReview', id: courseId },
        { type: 'CourseReviews', id: courseId },
        { type: 'Course', id: courseId },
      ],
    }),

    // ── ADMIN: модерация отзывов ────────────────────────────────
    getAdminReviews: builder.query({
      query: ({ status = 'pending', page = 1, limit = 20 } = {}) => {
        const qp = new URLSearchParams({ status, page, limit });
        return `/admin/reviews?${qp}`;
      },
      providesTags: ['AdminReviews'],
    }),
    moderateReview: builder.mutation({
      query: ({ reviewId, status, rejectionReason }) => ({
        url: `/admin/reviews/${reviewId}`,
        method: 'PATCH',
        body: { status, rejectionReason },
      }),
      // Инвалидируем админскую очередь + публичный список курса (новый
      // approved-отзыв должен сразу появиться).
      invalidatesTags: (result) => {
        const courseId = result?.review?.courseId;
        return [
          'AdminReviews',
          ...(courseId
            ? [
                { type: 'CourseReviews', id: courseId },
                { type: 'Course', id: courseId },
              ]
            : []),
        ];
      },
    }),

    // ── CERTIFICATES ────────────────────────────────────────────
    // Список моих сертификатов для страницы /my-certificates.
    getMyCertificates: builder.query({
      query: () => '/certificates/mine',
      providesTags: ['MyCertificates'],
    }),
    // Явный issue — если автохук не сработал по какой-то причине.
    // Использует POST с пустым body, courseId в URL.
    issueMyCertificate: builder.mutation({
      query: (courseId) => ({
        url: `/certificates/issue/${courseId}`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['MyCertificates', 'MyCourses'],
    }),
    // Публичная верификация по QR-токену. Без auth.
    verifyCertificate: builder.query({
      query: (token) => `/verify/${token}`,
      // Не кэшим долго — пользователь может перепроверить, ответ должен
      // отражать актуальный статус (revoked может смениться).
      keepUnusedDataFor: 30,
    }),

    // ── QUIZZES (owner) ─────────────────────────────────────────
    getMyQuizzes: builder.query({
      query: () => '/quizzes/my',
      providesTags: ['MyQuizzes'],
    }),
    getQuizById: builder.query({
      query: (id) => `/quizzes/${id}`,
      providesTags: (result, error, id) => [{ type: 'Quiz', id }],
    }),
    createQuiz: builder.mutation({
      query: (body) => ({ url: '/quizzes', method: 'POST', body }),
      invalidatesTags: ['MyQuizzes'],
    }),
    updateQuiz: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/quizzes/${id}`, method: 'PUT', body }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Quiz', id }, 'MyQuizzes'],
    }),
    deleteQuiz: builder.mutation({
      query: (id) => ({ url: `/quizzes/${id}`, method: 'DELETE' }),
      invalidatesTags: ['MyQuizzes'],
    }),
    getQuizStats: builder.query({
      query: (id) => `/quizzes/${id}/stats`,
      providesTags: (result, error, id) => [{ type: 'QuizStats', id }],
    }),
    getQuizQr: builder.query({
      query: (id) => `/quizzes/${id}/qr`,
    }),
    uploadQuizCover: builder.mutation({
      // Используем тот же XHR-based прогресс, что для аватаров.
      queryFn: buildUploadQueryFn(({ id }) => `/quizzes/${id}/cover`, 'cover'),
      invalidatesTags: (result, error, { id }) => [{ type: 'Quiz', id }, 'MyQuizzes'],
    }),
    deleteQuizCover: builder.mutation({
      query: (id) => ({ url: `/quizzes/${id}/cover`, method: 'DELETE' }),
      invalidatesTags: (result, error, id) => [{ type: 'Quiz', id }, 'MyQuizzes'],
    }),

    // ── QUIZZES (public, без авторизации) ───────────────────────
    getPublicQuiz: builder.query({
      query: (slug) => `/public/quizzes/${slug}`,
    }),
    startPublicQuiz: builder.mutation({
      query: ({ slug, participantName }) => ({
        url: `/public/quizzes/${slug}/start`,
        method: 'POST',
        body: { participantName },
      }),
    }),
    submitPublicQuiz: builder.mutation({
      query: ({ slug, attemptId, answers }) => ({
        url: `/public/quizzes/${slug}/submit`,
        method: 'POST',
        body: { attemptId, answers },
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useVerifyMutation,
  useGetMeQuery,
  useLazyGetMeQuery,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useRequestAccountDeletionMutation,
  useCancelAccountDeletionMutation,
  useGetMySessionsQuery,
  useRevokeSessionMutation,
  useGetAuditLogRecentQuery,
  useGetAuditLogQuery,
  useGetAuditLogMetaQuery,
  useGetNotificationsQuery,
  useGetNotificationUnreadCountQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useLogoutMutation,
  useGetInstructorCoursesQuery,
  useCreateCourseMutation,
  useGetCourseForEditingQuery,
  useUpdateCourseDetailsMutation,
  useUpdateCourseCurriculumMutation,
  useAddSectionMutation,
  useAddLessonMutation,
  useDeleteSectionMutation,
  useDeleteLessonMutation,
  useDeleteLessonVideoMutation,
  useDeletePreviewVideoMutation,
  useSubmitCourseForReviewMutation,
  useDeleteCourseMutation,
  useUploadAvatarMutation,
  useUploadCourseCoverMutation,
  useUploadLessonVideoMutation,
  useUploadPreviewVideoMutation,
  useGetMyCoursesQuery,
  useGetInstructorProfileQuery,
  useUpdateInstructorProfileMutation,
  useGetAllCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useGetPendingCoursesQuery,
  useUpdateCourseStatusMutation,
  useAdminDeleteCourseMutation,
  useGetAdminCourseFullQuery,
  useGetAdminDashboardQuery,
  useLazyGetAdminDashboardQuery,
  useReconcileStorageMutation,
  useGetAdminVideoJobsQuery,
  useLazyGetAdminVideoJobsQuery,
  useGetAdminUsersQuery,
  useUpdateAdminUserRolesMutation,
  useGetInstructorAnalyticsQuery,
  useGetInstructorOrdersQuery,
  useGetAdminOrdersQuery,
  useGetPublicCoursesQuery,
  useGetCourseByIdQuery,
   useGetInstructorPublicProfileQuery,
  useGetPreviewVideoQuery,
  useCheckCourseAccessQuery,
  useGetPurchasedCourseContentQuery,
  useUpdateLessonProgressMutation,
  useSubmitCourseLessonQuizMutation,
  usePurchaseCourseMutation,
  useGetOrderStatusQuery,
  useVerifyPaymentQuery,
  // p2p orders
  useRequestP2pOrderMutation,
  useGetMyPendingOrdersQuery,
  // instructor finance
  useGetInstructorFinanceOverviewQuery,
  useGetInstructorSalesQuery,
  useGetInstructorPayoutHistoryQuery,
  useRequestInstructorPayoutMutation,
  // admin p2p
  useGetAdminPendingOrdersQuery,
  useAdminMarkContactedMutation,
  useAdminConfirmOrderMutation,
  useAdminRejectOrderMutation,
  useAdminRefundOrderMutation,
  useGetAdminUserOrdersQuery,
  useGetAdminPendingPayoutsQuery,
  useAdminConfirmPayoutMutation,
  useAdminRejectPayoutMutation,
  // certificates
  useGetMyCertificatesQuery,
  useIssueMyCertificateMutation,
  useVerifyCertificateQuery,
  // reviews
  useGetCourseReviewsQuery,
  useGetMyReviewQuery,
  useCreateReviewMutation,
  useUpdateMyReviewMutation,
  useDeleteMyReviewMutation,
  useGetAdminReviewsQuery,
  useModerateReviewMutation,
  // quizzes
  useGetMyQuizzesQuery,
  useGetQuizByIdQuery,
  useCreateQuizMutation,
  useUpdateQuizMutation,
  useDeleteQuizMutation,
  useGetQuizStatsQuery,
  useGetQuizQrQuery,
  useUploadQuizCoverMutation,
  useDeleteQuizCoverMutation,
  useGetPublicQuizQuery,
  useStartPublicQuizMutation,
  useSubmitPublicQuizMutation,
} = apiSlice;